<template>
  <div>
    <b-card class="planner-sheet-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Planner Sheets</h5>
          <small class="text-muted">Manage class-wise planner sheets and replicate across sessions</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadData"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center flex-grow-1">
            <v-select
              v-model="filters.classID"
              :options="classes"
              label="name"
              :reduce="(val) => val.id"
              :clearable="true"
              placeholder="Class"
              class="filter-control filter-field-item"
              @input="onClassChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="subjects"
              label="subject"
              :reduce="(val) => val.id"
              :clearable="true"
              placeholder="Subject"
              :disabled="isTeacherScoped && !filters.classID"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.sessionID"
              :options="sessions"
              label="session"
              :reduce="(val) => val.id"
              :clearable="true"
              placeholder="Session"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <div class="filter-date-wrap">
              <flat-pickr
                v-model="dateRange"
                :config="dateConfig"
                class="form-control filter-control filter-date-item"
                placeholder="Date range"
                @on-change="onDateRangeChange"
              />
            </div>
          </div>

          <b-button
            class="action-btn-filter filter-clear-btn mx-50"
            v-b-tooltip.hover.top
            title="Clear filters"
            @click="clearFilters"
          >
            <span class="filter-clear-x" aria-hidden="true">×</span>
          </b-button>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <div v-if="canReplicate" class="position-relative replicate-action-wrap">
              <transition name="fade">
                <b-badge
                  v-if="persistentSelectedIds.length > 0"
                  variant="primary"
                  class="selection-dot-badge cursor-pointer"
                  @click="persistentSelectedIds = []"
                  v-b-tooltip.hover.top="'Clear All Selections'"
                >
                  {{ persistentSelectedIds.length }}
                </b-badge>
              </transition>

              <b-button
                variant="outline-primary"
                class="action-btn-filter d-flex align-items-center justify-content-center"
                :disabled="!persistentSelectedIds.length"
                v-b-tooltip.hover.top="'Replicate selected sheets'"
                style="min-width: 46px; border-color: #e0d8ff; background: #fff"
                @click="openReplicateModal"
              >
                <feather-icon icon="CopyIcon" size="18" />
              </b-button>
            </div>

            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="navigateToForm(0)"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Sheet</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="planner-sheet-table mt-1"
            :items="items"
            :fields="tableFields"
            :busy="dataLoading"
            show-empty
            :responsive="false"
            hover
            small
          >
            <template #empty>
              <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
                {{ $t("No records found") }}
              </h3>
            </template>

            <template #table-busy>
              <div class="sheet-skeleton">
                <div
                  v-for="n in skeletonRowCount"
                  :key="'skeleton-' + n"
                  class="sheet-skeleton__row"
                  :style="{
                    gridTemplateColumns: `repeat(${tableFields.length}, minmax(0, 1fr))`,
                  }"
                >
                  <div
                    v-for="(field, index) in tableFields"
                    :key="'skeleton-cell-' + n + '-' + index"
                    class="sheet-skeleton__cell"
                  >
                    <div
                      class="sheet-skeleton__bar"
                      :style="{ width: skeletonBarWidth(index) }"
                    ></div>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="canReplicate" #head(selected)>
              <b-form-checkbox
                :checked="isAllSelected"
                :indeterminate="isIndeterminate"
                @change="selectAllRows"
              />
            </template>

            <template v-if="canReplicate" #cell(selected)="data">
              <b-form-checkbox
                :checked="persistentSelectedIds.includes(data.item.id)"
                @change="toggleSelection(data.item.id)"
              />
            </template>

            <template #cell(sno)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>

            <template #cell(className)="data">
              <b-badge variant="light-primary">
                {{ data.value }}
              </b-badge>
            </template>

            <template #cell(subjectName)="data">
              <b-badge variant="light-success">
                {{ data.value }}
              </b-badge>
            </template>

            <template #cell(sessionName)="data">
              <b-badge variant="light-info">
                {{ data.value || "—" }}
              </b-badge>
            </template>

            <template #head(actions)="data">
              <div class="text-center">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="d-flex justify-content-center">
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  v-b-tooltip.hover.top
                  title="Edit"
                  class="action-btn-table mr-1"
                  @click="navigateToForm(data.item.id, data.item)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>

                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  v-b-tooltip.hover.top
                  title="Delete"
                  class="action-btn-outline delete-btn mr-1"
                  @click="deleteSheet(data.item.id)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>

                <b-button
                  variant="gradient-primary"
                  v-b-tooltip.hover.top
                  title="Print"
                  class="action-btn-table"
                  @click="printPlannerSheet(data.item)"
                >
                  <feather-icon icon="PrinterIcon" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Replicate Modal -->
    <b-modal
      v-model="showReplicateModal"
      title="Replicate Planner Sheets"
      centered
      size="lg"
      modal-class="replicate-modal"
      body-class="replicate-modal-body"
      no-close-on-backdrop
      :hide-header-close="replicating"
      @hidden="resetReplicateModal"
    >
      <div v-if="replicating" class="replicate-loading text-center py-4">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
        <p class="replicate-loading-title mt-3 mb-1">
          Replicating {{ persistentSelectedIds.length }} sheet(s)...
        </p>
        <p class="replicate-loading-subtitle mb-0">
          Please wait while sheets are copied to the selected session.
        </p>
      </div>

      <div v-else-if="replicateSummary" class="replicate-results">
        <div class="replicate-summary-card" :class="`is-${replicateAlertVariant}`">
          <div class="replicate-summary-title">{{ replicateSummaryTitle }}</div>
          <div class="replicate-summary-session">
            Destination session:
            <strong>{{ replicateSummary.sessionName || "—" }}</strong>
          </div>
        </div>

        <b-row class="replicate-stats mb-3 text-center">
          <b-col cols="6" md="3" class="mb-1">
            <div class="stat-box">
              <div class="stat-value">{{ replicateSummary.total }}</div>
              <div class="stat-label">Total</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="stat-box stat-success">
              <div class="stat-value">{{ replicateSummary.replicated }}</div>
              <div class="stat-label">Replicated</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="stat-box stat-warning">
              <div class="stat-value">{{ replicateSummary.skipped }}</div>
              <div class="stat-label">Skipped</div>
            </div>
          </b-col>
          <b-col cols="6" md="3" class="mb-1">
            <div class="stat-box stat-danger">
              <div class="stat-value">{{ replicateSummary.failed }}</div>
              <div class="stat-label">Failed</div>
            </div>
          </b-col>
        </b-row>

        <b-table
          v-if="replicateResults.length"
          :items="replicateResults"
          :fields="replicateResultFields"
          small
          responsive
          bordered
          show-empty
          empty-text="No result details available"
          class="replicate-results-table mb-0"
        >
          <template #cell(status)="data">
            <b-badge :variant="replicateStatusVariant(data.value)">
              {{ data.value }}
            </b-badge>
          </template>
          <template #cell(newId)="data">
            {{ data.value || "—" }}
          </template>
          <template #cell(existingId)="data">
            {{ data.value || "—" }}
          </template>
        </b-table>
      </div>

      <div v-else class="replicate-form">
        <div class="replicate-form-intro">
          <feather-icon icon="CopyIcon" size="20" class="replicate-form-icon" />
          <div>
            <div class="replicate-form-title">Choose destination session</div>
            <p class="replicate-form-text mb-0">
              Copy <strong>{{ persistentSelectedIds.length }}</strong> selected planner sheet(s) into the session below.
            </p>
          </div>
        </div>
        <b-form-group
          label="Destination Session"
          label-class="replicate-form-label"
          class="mb-0"
        >
          <v-select
            v-model="replicateSessionID"
            :options="sessions"
            label="session"
            :reduce="(val) => val.id"
            :clearable="false"
            placeholder="Select session"
            :loading="sessionsLoading"
            class="replicate-session-select"
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          :disabled="replicating"
          @click="showReplicateModal = false"
        >
          {{ replicateSummary ? "Close" : "Cancel" }}
        </b-button>
        <b-button
          v-if="!replicateSummary"
          variant="primary"
          :disabled="!replicateSessionID || replicating"
          @click="replicateSheets"
        >
          <feather-icon icon="CopyIcon" size="16" class="mr-50" />
          Replicate
        </b-button>
        <b-button
          v-else
          variant="primary"
          @click="finishReplicate"
        >
          Done
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BPagination,
  VBTooltip,
  BSpinner,
  BModal,
  BFormGroup,
  BAlert,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import Ripple from "vue-ripple-directive";
import axios from "axios";
import { mapActions } from "vuex";
import teacherPlannerScopeMixin from "./teacherPlannerScopeMixin";

export default {
  mixins: [teacherPlannerScopeMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BSpinner,
    BModal,
    BFormGroup,
    BAlert,
    BFormCheckbox,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async created() {
    // Initialize rights and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    await this.initTeacherPlannerScope();
    await this.loadClasses();
    if (!this.isTeacherScoped) {
      await this.loadAllPlannerSubjects();
      this.subjects = this.allSubjects;
    }
    this.loadSessions();
    this.loadData();
  },
  mounted() {
    this.updateScrollbarWidth();
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  updated() {
    this.updateScrollbarWidth();
  },
  data() {
    return {
      rights: {},
      dataLoading: true,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      selectedItem: null,
      persistentSelectedIds: [],
      showReplicateModal: false,
      replicateSessionID: null,
      replicating: false,
      sessionsLoading: false,
      replicateSummary: null,
      replicateResults: [],
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      classes: [],
      subjects: [],
      sessions: [],
      filters: {
        classID: null,
        subjectID: null,
        sessionID: null,
        dtFrom: "",
        dtTo: "",
      },
      dateRange: "",
      dateConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },

      items: [],

      replicateResultFields: [
        { key: "sourceId", label: "Source ID", thClass: "text-center", tdClass: "text-center" },
        { key: "status", label: "Status" },
        { key: "message", label: "Message" },
        { key: "newId", label: "New ID", thClass: "text-center", tdClass: "text-center" },
        { key: "existingId", label: "Existing ID", thClass: "text-center", tdClass: "text-center" },
      ],

      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
    };
  },
  computed: {
    canReplicate() {
      return !!(this.rights.add || this.rights.edit);
    },

    tableFields() {
      const fields = [
        {
          label: "S.No",
          key: "sno",
          thClass: "sticky-sno-2 text-center",
          tdClass: "sticky-sno-2 text-center",
        },
        { label: "Class", key: "className" },
        { label: "Subject", key: "subjectName" },
        { label: "Session", key: "sessionName" },
        { key: "actions", label: "Actions" },
      ];

      if (this.canReplicate) {
        return [
          { label: "", key: "selected", thClass: "sticky-sno", tdClass: "sticky-sno" },
          ...fields,
        ];
      }

      return fields;
    },

    isAllSelected() {
      if (!this.items.length) return false;
      return this.items.every((item) =>
        this.persistentSelectedIds.includes(item.id)
      );
    },

    isIndeterminate() {
      const selectedCount = this.items.filter((item) =>
        this.persistentSelectedIds.includes(item.id)
      ).length;
      return selectedCount > 0 && selectedCount < this.items.length;
    },

    skeletonRowCount() {
      return Math.min(Math.max(Number(this.perPage) || 10, 6), 10);
    },

    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start: Math.max(0, start), end: Math.max(0, end) };
    },

    replicateAlertVariant() {
      if (!this.replicateSummary) return "info";
      if (this.replicateSummary.failed > 0) return "danger";
      if (this.replicateSummary.skipped > 0) return "warning";
      return "success";
    },

    replicateSummaryTitle() {
      if (!this.replicateSummary) return "";
      const { replicated, skipped, failed } = this.replicateSummary;
      if (failed > 0) {
        return "Replication completed with some failures.";
      }
      if (skipped > 0 && replicated > 0) {
        return "Replication completed with some sheets skipped.";
      }
      if (skipped > 0 && replicated === 0) {
        return "All selected sheets were already in this session.";
      }
      return "All selected sheets replicated successfully.";
    },

    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },

    paginationInfo() {
      return {
        currentPage: this.currentPage,
        perPage: this.perPage,
        totalCount: this.totalCount,
        totalPages: Math.ceil(this.totalCount / this.perPage),
        showingItems: this.items.length
      };
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    skeletonBarWidth(index) {
      const widths = [
        "42%",
        "72%",
        "58%",
        "65%",
        "80%",
        "70%",
        "48%",
        "55%",
        "62%",
        "40%",
      ];
      return widths[index % widths.length];
    },

    async loadClasses() {
      try {
        if (this.isTeacherScoped) {
          this.classes = await this.loadTeacherClasses();
          return;
        }

        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        const list = res?.data || res || [];
        this.classes = Array.isArray(list) ? list.map((el) => el.cls).filter(Boolean) : [];
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
      }
    },

    async loadSubjects() {
      if (!this.isTeacherScoped) {
        await this.loadAllPlannerSubjects();
        this.subjects = this.allSubjects;
        return;
      }
      this.subjects = this.updateScopedSubjects(this.filters.classID);
    },

    async loadSessions() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data && res.data.status === "success" && Array.isArray(res.data.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    buildListUrl(page = this.currentPage, pageSize = this.perPage) {
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        pageNumber: String(page),
        pageSize: String(pageSize),
      });

      if (this.filters.classID) {
        params.set("classID", String(this.filters.classID));
      } else if (this.isTeacherScoped && this.assignedClassIDs.length > 0) {
        params.set("classIDs", this.assignedClassIDs.join(","));
      }
      if (this.filters.subjectID) {
        params.set("subjectID", String(this.filters.subjectID));
      }
      if (this.isTeacherScoped && this.teacherScopeTID) {
        params.set("tID", String(this.teacherScopeTID));
      }
      if (this.filters.sessionID) {
        params.set("sessionID", String(this.filters.sessionID));
      }
      if (this.filters.dtFrom) {
        params.set("dtFrom", this.filters.dtFrom);
      }
      if (this.filters.dtTo) {
        params.set("dtTo", this.filters.dtTo);
      }

      return (
        this.$store.state.domain +
        "PlannerSheet/GetByPagination/" +
        this.$store.state.userData.cId +
        "?" +
        params.toString()
      );
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadData();
      this.$nextTick(() => {
        this.updateScrollbarWidth();
      });
    },

    syncScroll() {
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar.querySelector(
          ".table-scrollbar-content"
        );

        if (scrollContent) {
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (scrollContent) {
            if (tableWrapper.scrollWidth > tableWrapper.clientWidth) {
              scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
              topScrollbar.style.display = "block";

              topScrollbar.onscroll = () => {
                tableWrapper.scrollLeft = topScrollbar.scrollLeft;
              };
            } else {
              topScrollbar.style.display = "none";
            }
          }
        }
      });
    },

    onClassChange(classID) {
      if (this.isTeacherScoped) {
        this.filters.subjectID = null;
      }
      this.subjects = this.updateScopedSubjects(classID);
      this.invalidateTeacherScopedCache();
      this.onFilterChange();
    },

    onFilterChange() {
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      this.loadData();
    },

    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        this.filters.dtFrom = this.formatDateForInput(selectedDates[0]);
        this.filters.dtTo = this.formatDateForInput(selectedDates[1]);
        this.onFilterChange();
      } else if (!selectedDates || selectedDates.length === 0) {
        if (this.filters.dtFrom || this.filters.dtTo) {
          this.filters.dtFrom = "";
          this.filters.dtTo = "";
          this.onFilterChange();
        }
      }
    },

    formatDateForInput(date) {
      const d = new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    clearFilters() {
      this.filters.classID = null;
      this.filters.subjectID = null;
      this.filters.sessionID = null;
      this.filters.dtFrom = "";
      this.filters.dtTo = "";
      this.dateRange = "";
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      this.loadData();
    },

    async loadData() {
      this.dataLoading = true;
      try {
        if (this.isTeacherScoped) {
          const cacheKey = [
            this.filters.classID,
            this.filters.subjectID,
            this.filters.sessionID,
            this.filters.dtFrom,
            this.filters.dtTo,
            this.assignedClassIDs.join(","),
          ].join("|");
          const allItems = await this.fetchTeacherScopedPaginatedData({
            cacheKey,
            fetchPage: (p, ps) =>
              this.get({
                url: this.buildListUrl(p, ps),
                token: this.$store.state.userData.token,
                dashboard: true,
              }),
          });
          const { items, totalCount } = this.getTeacherScopedPageSlice(allItems);
          this.items = items;
          this.totalCount = totalCount;
          return;
        }

        const obj = {
          url: this.buildListUrl(),
          token: this.$store.state.userData.token,
          dashboard: true,
        };

        let res = await this.get(obj);

        if (res && Array.isArray(res.data)) {
          this.items = res.data;
          this.totalCount = res.total || res.totalCount || res.totalRecords || 0;
        } else if (res && Array.isArray(res)) {
          this.items = res;
          this.totalCount = res.length;
        } else {
          this.items = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading data:", error);
        this.items = [];
        this.totalCount = 0;
        this.$bvToast.toast("Error loading planner sheets. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
        this.$nextTick(() => {
          this.updateScrollbarWidth();
        });
      }
    },

    navigateToForm(id = 0, data = null) {
      if (id === 0) {
        // Add mode
        this.$router.push({
          name: "planner-sheet-form",
          params: { id: 0 },
        });
      } else {
        // Edit mode
        this.$router.push({
          name: "planner-sheet-form",
          params: { id: id },
        });
      }
    },

    async deleteSheet(id) {
      const result = await this.$swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerSheet/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Planner sheet deleted successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };

        const success = await this.delete(obj);
        if (success) {
          this.loadData();
        }
      }
    },

    toggleSelection(id) {
      const index = this.persistentSelectedIds.indexOf(id);
      if (index === -1) {
        this.persistentSelectedIds.push(id);
      } else {
        this.persistentSelectedIds.splice(index, 1);
      }
    },

    selectAllRows(checked) {
      if (checked) {
        this.items.forEach((item) => {
          if (!this.persistentSelectedIds.includes(item.id)) {
            this.persistentSelectedIds.push(item.id);
          }
        });
      } else {
        const currentIds = this.items.map((item) => item.id);
        this.persistentSelectedIds = this.persistentSelectedIds.filter(
          (id) => !currentIds.includes(id)
        );
      }
    },

    getDefaultSessionID() {
      if (!this.sessions.length) return null;
      const currentSession = this.sessions.find(
        (el) => el.isCurrentSession === true
      );
      return currentSession
        ? currentSession.id
        : this.sessions[this.sessions.length - 1].id;
    },

    async openReplicateModal() {
      if (!this.persistentSelectedIds.length) return;

      this.replicateSummary = null;
      this.replicateResults = [];
      this.replicateSessionID = this.getDefaultSessionID();
      this.showReplicateModal = true;

      if (!this.sessions.length) {
        this.sessionsLoading = true;
        try {
          await this.loadSessions();
          this.replicateSessionID = this.getDefaultSessionID();
        } finally {
          this.sessionsLoading = false;
        }
      }
    },

    resetReplicateModal() {
      if (this.replicating) return;
      this.replicateSessionID = null;
      this.replicateSummary = null;
      this.replicateResults = [];
    },

    finishReplicate() {
      this.showReplicateModal = false;
      this.persistentSelectedIds = [];
      this.selectedItem = null;
      this.loadData();
    },

    replicateStatusVariant(status) {
      const value = String(status || "").toLowerCase();
      if (value === "success") return "success";
      if (value === "skipped") return "warning";
      if (value === "failed" || value === "error") return "danger";
      return "secondary";
    },

    async replicateSheets() {
      if (!this.replicateSessionID || !this.persistentSelectedIds.length) {
        this.$bvToast.toast("Please select a destination session.", {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      const sourceIds = this.persistentSelectedIds
        .map((id) => Number(id))
        .filter((id) => id > 0);

      if (!sourceIds.length) {
        this.$bvToast.toast("No valid planner sheets selected.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.replicating = true;
      this.replicateSummary = null;
      this.replicateResults = [];

      try {
        const campusID = this.$store.state.userData.cId;
        const params = new URLSearchParams({
          sessionID: String(this.replicateSessionID),
          db: this.$store.state.userData.db,
          userID: String(this.$store.state.userData.userID),
        });

        const url = `${this.$store.state.domain}PlannerSheet/Replicate/${campusID}?${params.toString()}`;

        const response = await axios.post(url, sourceIds, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            "Content-Type": "application/json",
          },
        });

        const payload = response.data || {};
        const resultData = payload.data || {};

        if (
          payload.status === "success" ||
          payload.status === "partial" ||
          Array.isArray(resultData.results)
        ) {
          this.replicateSummary = {
            sessionID: resultData.sessionID || this.replicateSessionID,
            sessionName: resultData.sessionName || this.getSessionName(this.replicateSessionID),
            total: resultData.total ?? sourceIds.length,
            replicated: resultData.replicated ?? 0,
            skipped: resultData.skipped ?? 0,
            failed: resultData.failed ?? 0,
          };
          this.replicateResults = Array.isArray(resultData.results)
            ? resultData.results
            : [];

          const toastVariant =
            this.replicateSummary.failed > 0
              ? "danger"
              : this.replicateSummary.skipped > 0
              ? "warning"
              : "success";

          this.$bvToast.toast(this.replicateSummaryTitle, {
            title: "Replication Complete",
            variant: toastVariant,
            solid: true,
            autoHideDelay: 5000,
          });
        } else {
          throw new Error(payload.message || payload.data || "Replication failed.");
        }
      } catch (error) {
        console.error("Error replicating planner sheets:", error);
        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          "Failed to replicate planner sheets. Please try again.";

        this.$bvToast.toast(
          typeof message === "string" ? message : "Failed to replicate planner sheets.",
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.replicating = false;
      }
    },

    getSessionName(sessionID) {
      const session = this.sessions.find((s) => s.id === sessionID);
      return session ? session.session : "—";
    },

    async printPlannerSheet(item = null) {
      const plannerItem = item || this.selectedItem;
      if (!plannerItem) return;

      if (this.reportDomain === "myskoolaba") {
        const route = this.$router.resolve({
          name: "planner-sheet-aba",
          query: {
            id: String(plannerItem.id),
            db: this.$store.state.userData.db,
            cID: String(this.$store.state.userData.cId),
            className: plannerItem.className || "",
            subjectName: plannerItem.subjectName || "",
            sessionName: plannerItem.sessionName || "",
          },
        });
        window.open(route.href, "_blank", "noopener,noreferrer");
        return;
      }

      if (this.reportDomain === "myskooldeenway") {
        const route = this.$router.resolve({
          name: "planner-sheet-deenway",
          query: {
            id: String(plannerItem.id),
            db: this.$store.state.userData.db,
            cID: String(this.$store.state.userData.cId),
            className: plannerItem.className || "",
            subjectName: plannerItem.subjectName || "",
            sessionName: plannerItem.sessionName || "",
            campusName:
              this.$store.state.userData?.campusName ||
              this.$store.state.userData?.organizationName ||
              "",
          },
        });
        window.open(route.href, "_blank", "noopener,noreferrer");
        return;
      }

      const route = this.$router.resolve({
        name: "planner-sheet-print",
        query: {
          id: String(plannerItem.id),
          db: this.$store.state.userData.db,
          cID: String(this.plannerCampusID),
          className: plannerItem.className || "",
          subjectName: plannerItem.subjectName || "",
          sessionName: plannerItem.sessionName || "",
        },
      });
      window.open(route.href, "_blank", "noopener,noreferrer");
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
.planner-sheet-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  }
}

.resource-title {
  color: #2d1b69;
  font-weight: 800;
  letter-spacing: -0.5px;
  font-size: 1.4rem;
}

.filter-row {
  padding: 0.5rem 0;
  border-radius: 15px;
}

.filter-toolbar {
  gap: 0.5rem;
  min-width: 0;
  flex-wrap: nowrap;
}

.filter-fields-group {
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: nowrap;
}

.filter-actions-group {
  gap: 0.5rem;
  flex-shrink: 0;
}

.filter-fields-group--cols-4 {
  > .filter-field-item,
  > .filter-date-wrap {
    flex: 1 1 0;
    min-width: 0;
    width: auto !important;
    max-width: none !important;
  }
}

.filter-date-wrap {
  display: flex;
  align-items: center;
  min-width: 0;

  ::v-deep .flatpickr-wrapper {
    width: 100%;
  }

  ::v-deep input.flatpickr-input,
  ::v-deep .filter-date-item {
    width: 100% !important;
    height: 46px !important;
    min-height: 46px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 0.75rem !important;
    font-size: 0.875rem !important;
    font-weight: 500;
    color: #2d1b69;
    background: #fff !important;
    box-shadow: none !important;
  }

  ::v-deep input.form-control:not(.flatpickr-input) {
    position: absolute !important;
    opacity: 0 !important;
    pointer-events: none !important;
    height: 0 !important;
    width: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    margin: 0 !important;
  }
}

.filter-clear-btn {
  flex: 0 0 46px !important;
  width: 46px !important;
  min-width: 46px !important;
  max-width: 46px !important;
  background: #fff !important;
  border: 2px solid #7367f0 !important;
  color: #7367f0 !important;
  padding: 0 !important;

  .filter-clear-x {
    font-size: 1.4rem;
    line-height: 1;
    font-weight: 700;
    color: #7367f0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -1px;
  }

  &:hover {
    background: #f3f0ff !important;
    border-color: #7c5dfa !important;

    .filter-clear-x {
      color: #7c5dfa;
    }
  }
}

.add-sheet-btn {
  padding-left: 1rem;
  padding-right: 1rem;
}

.replicate-action-wrap {
  flex-shrink: 0;
}

.filter-control {
  ::v-deep .vs__dropdown-toggle {
    height: 46px;
    border-radius: 12px;
    border: 1.5px solid #e0d8ff;
    background: #ffffff;
    padding-top: 2px;
    transition: all 0.3s ease;

    &:hover {
      border-color: #7367f0;
    }
  }

  ::v-deep .vs__selected {
    font-weight: 500;
    color: #2d1b69;
  }

  ::v-deep .vs__search::placeholder {
    color: #b4a9e0;
  }

  &.form-control {
    height: 46px;
    border-radius: 12px;
    border: 1.5px solid #e0d8ff;
    padding-left: 1rem;
    font-weight: 500;
    color: #2d1b69;
    transition: all 0.3s ease;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    }
  }
}

.action-btn-filter {
  height: 46px;
  min-width: 46px;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-color: #e0d8ff !important;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);
  }

  &:disabled {
    opacity: 0.55;
  }
}

.selection-dot-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 22px;
  height: 22px;
  border-radius: 10px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 6px !important;
  font-size: 0.7rem !important;
  z-index: 10;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #7367f0 !important;
  color: #fff !important;
}

.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    display: flex !important;
    align-items: center !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected-options {
    display: flex !important;
    align-items: center !important;
    padding: 0 !important;
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    height: 100%;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.sheet-skeleton {
  width: 100%;
  padding: 0.35rem 0.25rem 0.75rem;
}

.sheet-skeleton__row {
  display: grid;
  gap: 0.75rem;
  align-items: center;
  padding: 0.9rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.sheet-skeleton__cell {
  min-width: 0;
}

.sheet-skeleton__bar {
  height: 14px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #ecebf8 0%,
    #f7f6ff 40%,
    #ecebf8 80%
  );
  background-size: 200% 100%;
  animation: sheetSkeletonShine 1.2s ease-in-out infinite;
}

@keyframes sheetSkeletonShine {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@media (max-width: 992px) {
  .pagination-controls-top {
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
}

@media (max-width: 992px) {
  .filter-toolbar {
    flex-wrap: wrap;
  }

  .filter-fields-group {
    flex-wrap: wrap;
    width: 100%;
  }

  .filter-actions-group {
    width: 100%;
    justify-content: flex-end;
    margin-left: 0 !important;
  }
}

@media (max-width: 768px) {
  .filter-fields-group--cols-4 {
    > .filter-field-item,
    > .filter-date-wrap {
      flex: 1 1 calc(50% - 0.5rem);
    }
  }

  .planner-sheet-list-card {
    padding: 1rem;
    border-radius: 15px;
  }
}

.replicate-stats {
  .stat-box {
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    padding: 0.75rem 0.5rem;
    background: #fff;

    .stat-value {
      font-size: 1.35rem;
      font-weight: 700;
      line-height: 1.2;
      color: #2f2b3d;
    }

    .stat-label {
      font-size: 0.78rem;
      color: #6e6b7b;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
  }

  .stat-success .stat-value {
    color: #28c76f;
  }

  .stat-warning .stat-value {
    color: #ff9f43;
  }

  .stat-danger .stat-value {
    color: #ea5455;
  }
}

.planner-sheet-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: 100%;
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-spacing: 0;

  &.b-table-busy {
    min-height: 320px;
  }

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;

      td {
        background: #f3f0ff !important;
        color: #2d1b69;
      }
    }
  }

  ::v-deep .sticky-sno {
    min-width: 44px;
    max-width: 44px;
    text-align: center;
  }

  ::v-deep .sticky-sno-2 {
    min-width: 60px;
    text-align: center;
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: #fff !important;

  &.delete-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;

    &:hover {
      background: #fef2f2 !important;
      transform: translateY(-2px);
    }
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

::v-deep .replicate-modal {
  .modal-dialog,
  .modal-content,
  .modal-body {
    overflow: visible;
  }

  .modal-header {
    border-bottom: 1px solid #ebe9f1;
    padding: 1rem 1.25rem;

    .modal-title {
      color: #2f2b3d;
      font-weight: 700;
      font-size: 1.1rem;
    }
  }

  .modal-footer {
    border-top: 1px solid #ebe9f1;
    padding: 0.85rem 1.25rem;
    position: relative;
    z-index: 0;
  }
}

.replicate-form {
  position: relative;
  z-index: 1;
}

.replicate-modal-body {
  color: #2f2b3d;
  padding: 1.25rem;
}

.replicate-loading-title {
  color: #2f2b3d;
  font-weight: 700;
  font-size: 1rem;
}

.replicate-loading-subtitle {
  color: #6e6b7b;
  font-size: 0.9rem;
}

.replicate-form-intro {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid #dbe7f7;
  border-radius: 10px;
  background: #f7faff;
}

.replicate-form-icon {
  color: #7367f0;
  flex-shrink: 0;
  margin-top: 2px;
}

.replicate-form-title {
  color: #2f2b3d;
  font-weight: 700;
  font-size: 0.98rem;
  margin-bottom: 0.25rem;
}

.replicate-form-text {
  color: #5e5873;
  font-size: 0.92rem;
  line-height: 1.45;
}

::v-deep .replicate-form-label {
  color: #2f2b3d !important;
  font-weight: 700 !important;
  font-size: 0.9rem !important;
  margin-bottom: 0.5rem !important;
}

::v-deep .replicate-session-select {
  .vs__dropdown-toggle {
    min-height: 42px;
    border-color: #d8d6de;
    background: #fff;
  }

  .vs__selected,
  .vs__search {
    color: #2f2b3d;
    font-size: 0.95rem;
  }
}

.replicate-summary-card {
  border-radius: 10px;
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;

  &.is-success {
    background: #e8f8ef;
    border-color: #b9e8c9;
  }

  &.is-warning {
    background: #fff6e8;
    border-color: #ffd9a8;
  }

  &.is-danger {
    background: #fdecec;
    border-color: #f5b5b5;
  }

  &.is-info {
    background: #eef4ff;
    border-color: #c8d9fb;
  }
}

.replicate-summary-title {
  color: #2f2b3d;
  font-weight: 700;
  font-size: 0.98rem;
  margin-bottom: 0.35rem;
}

.replicate-summary-session {
  color: #5e5873;
  font-size: 0.9rem;

  strong {
    color: #2f2b3d;
  }
}

::v-deep .replicate-results-table {
  thead th {
    background: #f3f4f6 !important;
    color: #2f2b3d !important;
    font-weight: 700;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-color: #e4e6ea !important;
  }

  tbody td {
    color: #2f2b3d !important;
    font-size: 0.88rem;
    vertical-align: middle;
    border-color: #eceef1 !important;
  }
}

</style>
